import { useEffect } from "react";
import { ArrowLeft, Mail, MapPin, Phone } from "lucide-react";
import { useRegionalExperience } from "../hooks/useRegionalExperience";
import { missionSiteFallback } from "../data/missionContent";

export default function Transparency() {
  const { siteConfig } = useRegionalExperience();
  const disclosures = {
    ...missionSiteFallback.disclosures,
    ...(siteConfig.disclosures || {}),
  };
  const sections =
    Array.isArray(disclosures.sections) && disclosures.sections.length
      ? disclosures.sections
      : missionSiteFallback.disclosures.sections;
  const fundUse =
    Array.isArray(disclosures.fundUse) && disclosures.fundUse.length
      ? disclosures.fundUse
      : missionSiteFallback.disclosures.fundUse;
  const contact = disclosures.contact || missionSiteFallback.disclosures.contact;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#fcf9f5] to-white">
      <div className="relative flex h-[44vh] items-center justify-center overflow-hidden bg-[#7B0000] text-white">
        <div className="relative z-20 px-4 text-center">
          <h1 className="mb-4 mt-7 font-serif text-4xl md:text-6xl">Transparency &amp; Disclosures</h1>
          <p className="mx-auto max-w-3xl text-lg font-light md:text-2xl">
            How we steward your trust and your contributions.
          </p>
        </div>
        <div className="absolute bottom-4 right-4 z-10 font-serif text-6xl text-white/20">ॐ</div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <a
          href="/"
          className="mb-8 inline-flex items-center text-[#B82A1E] transition-colors duration-300 hover:text-[#8a1f16]"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to home
        </a>

        <div className="mx-auto max-w-3xl space-y-10 pb-16">
          {sections.map((section, i) => (
            <section key={i}>
              {section.heading && (
                <h2 className="mb-3 text-2xl font-semibold text-[#14201f]">{section.heading}</h2>
              )}
              {section.body && <p className="text-base leading-8 text-[#52615f]">{section.body}</p>}
            </section>
          ))}

          {fundUse.length > 0 && (
            <section>
              <h2 className="mb-4 text-2xl font-semibold text-[#14201f]">How funds are used</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {fundUse.map((row, i) => (
                  <div key={i} className="rounded-lg border border-[#dfe8e6] bg-white p-5">
                    <p className="font-semibold text-[#14201f]">{row.label}</p>
                    {row.note && <p className="mt-2 text-sm leading-7 text-[#52615f]">{row.note}</p>}
                  </div>
                ))}
              </div>
            </section>
          )}

          <section className="rounded-lg border border-[#dfe8e6] bg-[#f7faf9] p-6">
            <h2 className="mb-4 text-2xl font-semibold text-[#14201f]">Contact</h2>
            <div className="space-y-3 text-sm text-[#52615f]">
              {contact.email && (
                <a href={`mailto:${contact.email}`} className="flex items-center gap-3 hover:text-[#14201f]">
                  <Mail className="h-4 w-4 text-[#9d1f1c]" />
                  {contact.email}
                </a>
              )}
              {contact.phone && (
                <p className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-[#9d1f1c]" />
                  {contact.phone}
                </p>
              )}
              {contact.address && (
                <p className="flex items-center gap-3">
                  <MapPin className="h-4 w-4 text-[#9d1f1c]" />
                  {contact.address}
                </p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
